
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { AppDispatch } from '../../../../store';
import AlertPopup from '../../../../components/common/AlertPopup';
import ActionModal from '../../../../components/ActionModal';
import ConfirmationDialog from '../../../../components/common/ConfirmationDialog';
import CancelShift from './components/CancelShift';
import ExtendShift from './components/ExtendShift';
import ValidationConflicts from './components/ValidationConflicts';
import { fetchShiftDetails, rebroadcastShift, cancelFacilityShift, forceCancelFacilityShift, extendShiftTime, validateExtendShiftTime, deleteCancelledShift } from '../../../../modules/facilityShifts/actions';
import { bulkOrganizationApproveAttendance, organizationClockIn, organizationClockOut } from '../../../../modules/shift/actions';
import { selectShiftDetailsData, selectShiftDetailsLoading } from '../../../../modules/facilityShifts/selectors';
import { updateWorkerViolationFlag } from '../../../../modules/worker';

import {
  Divider
} from './styles';

import WorkerAssigned from './components/WorkerAssigned';
import AssignedWorkerList from './components/AssignedWorkerList';
import FacilityDetails from './components/FacilityDetails';
import ShiftDetails from './components/ShiftDetails';
import ShiftActions from './components/ShiftActions';
import ManualAssign from './components/ManualAssign';
import ReplaceWorker from './components/ReplaceWorker';
import { Box, CircularProgress, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, TextField } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import VerifyShift from '../../../../pages/FacilityShift/List/components/WorkerAssigned/components/VerifyShift';

interface ShiftDetailContentProps {
  shiftId: number | null;
  facilityName?: string;
  onEdit?: (shift: any) => void;
  onRefresh?: () => void;
}

const ShiftDetailContent: React.FC<ShiftDetailContentProps> = ({ shiftId, facilityName, onEdit, onRefresh }) => {
  const dispatch = useDispatch<AppDispatch>();
  const shift = useSelector(selectShiftDetailsData);
  const loading = useSelector(selectShiftDetailsLoading);
  const [view, setView] = React.useState<'details' | 'manual_assign' | 'worker_detail' | 'replace_worker'>('details');
  const [selectedAssignment, setSelectedAssignment] = React.useState<any>(null);
  const [alertState, setAlertState] = React.useState({ open: false, severity: 'success' as 'success' | 'error', message: '' });
  const [openActionModal, setOpenActionModal] = React.useState(false);
  const [openConfirmation, setOpenConfirmation] = React.useState(false);
  const [openForceConfirmation, setOpenForceConfirmation] = React.useState(false);
  const [cancelData, setCancelData] = React.useState<{ reason: string; notes: string; is_free_cancellation?: boolean } | null>(null);
  const [isVerifyModalOpen, setIsVerifyModalOpen] = React.useState(false);
  const [openExtendModal, setOpenExtendModal] = React.useState(false);
  const [extending, setExtending] = React.useState(false);

  // Validation State
  const [validationConflicts, setValidationConflicts] = React.useState<any[]>([]);
  const [openValidationModal, setOpenValidationModal] = React.useState(false);
  const [pendingExtendAuth, setPendingExtendAuth] = React.useState<{ shiftTime: string } | null>(null);

  // Flag Worker State
  const [flagConfirmationOpen, setFlagConfirmationOpen] = React.useState(false);
  const [workerToFlag, setWorkerToFlag] = React.useState<number | null>(null);
  const [isFlagging, setIsFlagging] = React.useState(false);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = React.useState(false);
  const [openApproveConfirmation, setOpenApproveConfirmation] = React.useState(false);
  const [approvalRemarks, setApprovalRemarks] = React.useState('');
  const [isDeleting, setIsDeleting] = React.useState(false);
  
  useEffect(() => {
    if (shift && selectedAssignment) {
      const refreshedAssignment = shift.shift_assignment?.find((a: any) => 
        (a.assignment_id || a.id) === (selectedAssignment.assignment_id || selectedAssignment.id)
      );
      if (refreshedAssignment) {
        setSelectedAssignment(refreshedAssignment);
      }
    }
  }, [shift, selectedAssignment]);

  const handleBroadcast = () => {
    if (shiftId) {
      // @ts-ignore
      dispatch(rebroadcastShift(shiftId))
        .unwrap()
        .then((res: { message: any; }) => {
          setAlertState({ open: true, severity: 'success', message: res.message || 'Notification re-broadcasted successfully.' });
          // @ts-ignore
          dispatch(fetchShiftDetails(shiftId));
          onRefresh && onRefresh();
        })
        .catch((err: any) => {
          setAlertState({ open: true, severity: 'error', message: String(err) || 'Failed to re-broadcast notification.' });
        });
    }
  };

  const onCancelShiftClick = () => {
    setOpenActionModal(true);
  };

  const handleCancelSubmit = (data: { reason: string; notes: string; is_free_cancellation: boolean }) => {
    setCancelData(data);
    setOpenConfirmation(true);
  };

  const handleConfirmCancel = () => {
    if (cancelData && shiftId) {
      // @ts-ignore
      dispatch(cancelFacilityShift({ shiftId: Number(shiftId), reason: cancelData.reason, additionalReason: cancelData.notes, is_free_cancellation: cancelData.is_free_cancellation }))
        .unwrap()
        .then((res: any) => {
          setOpenActionModal(false);
          setOpenConfirmation(false);
          setAlertState({ open: true, severity: 'success', message: res.message || 'Shift cancelled successfully' });
          // @ts-ignore
          dispatch(fetchShiftDetails(shiftId));
          onRefresh && onRefresh();
        })
        .catch((err: any) => {
          setOpenConfirmation(false);
          if (err === "Shift cancellation time has passed, if you cancel now charge may applied") {
            setOpenForceConfirmation(true);
          } else {
            setAlertState({ open: true, severity: 'error', message: String(err) });
          }
        });
    }
  };

  const handleForceCancel = () => {
    if (cancelData && shiftId) {
      // @ts-ignore
      dispatch(forceCancelFacilityShift({ shiftId: Number(shiftId), cancellation_reason: cancelData.reason }))
        .unwrap()
        .then((res: any) => {
          setOpenActionModal(false);
          setOpenForceConfirmation(false);
          setAlertState({ open: true, severity: 'success', message: res.message || 'Shift force cancelled successfully' });
          // @ts-ignore
          dispatch(fetchShiftDetails(shiftId));
          onRefresh && onRefresh();
        })
        .catch((err: any) => {
          setAlertState({ open: true, severity: 'error', message: String(err) });
        });
    }
  };

  const handleDeleteShift = () => {
    if (shiftId) {
      setIsDeleting(true);
      // @ts-ignore
      dispatch(deleteCancelledShift(Number(shiftId)))
        .unwrap()
        .then((res: any) => {
          setOpenDeleteConfirmation(false);
          setAlertState({ open: true, severity: 'success', message: res.message || 'Shift deleted successfully' });
          onRefresh && onRefresh();
        })
        .catch((err: any) => {
          setOpenDeleteConfirmation(false);
          setAlertState({ open: true, severity: 'error', message: String(err) });
        })
        .finally(() => {
          setIsDeleting(false);
        });
    }
  };

  const handleApproveShift = () => {
    if (shift && shift.shift_assignment && shiftId) {
      const assignmentIds = shift.shift_assignment
        .filter((a: any) => ['facility_approval_pending', 'partially_approval_pending', 'approval_pending'].includes(a.status))
        .map((a: any) => a.assignment_id || a.id);

      if (assignmentIds.length === 0) {
        setAlertState({ open: true, severity: 'error', message: 'No pending assignments found to approve.' });
        return;
      }
      setOpenApproveConfirmation(true);
    }
  };

  const onConfirmApprove = () => {
    if (shiftId) {
      dispatch(bulkOrganizationApproveAttendance({
        shift_ids: [Number(shiftId)],
        remarks: approvalRemarks
      }))
        .unwrap()
        .then((res: any) => {
          setAlertState({ open: true, severity: 'success', message: res.message || 'Shift approved successfully' });
          setOpenApproveConfirmation(false);
          setApprovalRemarks('');
          dispatch(fetchShiftDetails(Number(shiftId)));
          onRefresh && onRefresh();
        })
        .catch((err: any) => {
          setAlertState({ open: true, severity: 'error', message: String(err) });
        });
    }
  };

  const handleClockIn = (assignmentId: number) => {
    dispatch(organizationClockIn(assignmentId))
      .unwrap()
      .then((res: any) => {
        setAlertState({ open: true, severity: 'success', message: res.message || 'Clocked in successfully' });
        if (shiftId) dispatch(fetchShiftDetails(Number(shiftId)));
        onRefresh && onRefresh();
      })
      .catch((err: any) => {
        setAlertState({ open: true, severity: 'error', message: String(err) });
      });
  };

  const handleClockOut = (assignmentId: number) => {
    dispatch(organizationClockOut(assignmentId))
      .unwrap()
      .then((res: any) => {
        setAlertState({ open: true, severity: 'success', message: res.message || 'Clocked out successfully' });
        if (shiftId) dispatch(fetchShiftDetails(Number(shiftId)));
        onRefresh && onRefresh();
      })
      .catch((err: any) => {
        setAlertState({ open: true, severity: 'error', message: String(err) });
      });
  };

  const handleFlagWorkerClick = (workerId: number) => {
    // alert(`Parent received flag click for worker: ${workerId}`);
    setWorkerToFlag(workerId);
    setFlagConfirmationOpen(true);
  };

  const confirmFlagWorker = () => {
    if (workerToFlag) {
      setIsFlagging(true);
      // @ts-ignore
      dispatch(updateWorkerViolationFlag({ workerId: workerToFlag, violation_flag: true }))
        .unwrap()
        .then((res: { data: { message: any; }; }) => {
          setFlagConfirmationOpen(false);
          setAlertState({ open: true, severity: 'success', message: res.data?.message || 'Worker flagged successfully' });
          if (shiftId) dispatch(fetchShiftDetails(shiftId));
        })
        .catch((err: any) => {
          setAlertState({ open: true, severity: 'error', message: String(err) });
        })
        .finally(() => {
          setIsFlagging(false);
        });
    }
  };



  const executeExtendShift = (shiftTime: string, force: boolean = false) => {
    if (shiftId && shift) {
      setExtending(true);
      // @ts-ignore
      dispatch(extendShiftTime({
        shiftId: Number(shiftId),
        facilityId: (shift.facility as any)?.facility_id || (shift as any).facility_id,
        shiftTime,
        force
      }))
        .unwrap()
        .then((res: { message: any; }) => {
          setOpenExtendModal(false);
          setOpenValidationModal(false);
          setAlertState({ open: true, severity: 'success', message: res.message || 'Shift extended successfully' });
          // @ts-ignore
          dispatch(fetchShiftDetails(shiftId));
          onRefresh && onRefresh();
        })
        .catch((err: any) => {
          if (err.conflicts || (err.data && err.data.conflicts)) {
            setValidationConflicts(err.conflicts || err.data.conflicts);
            setPendingExtendAuth({ shiftTime });
            setOpenExtendModal(false);
            setOpenValidationModal(true);
          } else {
            setAlertState({ open: true, severity: 'error', message: String(err.message || err) });
          }
        })
        .finally(() => {
          setExtending(false);
        });
    }
  };

  const handleExtendSubmit = (shiftTime: string) => {
    if (shiftId && shift) {
      setExtending(true);

      // Validate first
      // @ts-ignore
      dispatch(validateExtendShiftTime({
        shiftId: Number(shiftId),
        shiftTime
      }))
        .unwrap()
        .then((res: { valid?: boolean; status?: boolean; conflicts?: any[]; message?: string }) => {
          // Check for valid: true OR (status: true AND no conflicts)
          const isValid = res.valid === true || (res.status === true && (!res.conflicts || res.conflicts.length === 0));

          if (isValid) {
            executeExtendShift(shiftTime);
          } else {
            // Generic failure in 200 response (if any)
            setAlertState({ open: true, severity: 'error', message: res.message || "Validation failed" });
            setExtending(false);
          }
        })
        .catch((err: any) => {
          // Check if error response contains conflicts (status 409)
          if (err.data && err.data.conflicts) {
            setValidationConflicts(err.data.conflicts);
            setPendingExtendAuth({ shiftTime });
            setOpenExtendModal(false);
            setOpenValidationModal(true);
            setExtending(false);
          } else if (err.conflicts) {
            // Some redux wrappers might return the data directly in err
            setValidationConflicts(err.conflicts);
            setPendingExtendAuth({ shiftTime });
            setOpenExtendModal(false);
            setOpenValidationModal(true);
            setExtending(false);
          } else {
            setAlertState({ open: true, severity: 'error', message: "Validation failed: " + (err.message || String(err)) });
            setExtending(false);
          }
        });
    }
  };

  const handleValidationProceed = () => {
    if (pendingExtendAuth) {
      executeExtendShift(pendingExtendAuth.shiftTime, true);
    }
  };

  const handleWorkerClick = (assignment: any) => {
    setSelectedAssignment(assignment);
    setView('worker_detail');
  };

  const handleReplaceWorkerStart = (workerId: number) => {
    if (!shift) return;
    if (!selectedAssignment || selectedAssignment.worker?.worker_id !== workerId) {
      const assignment = shift.shift_assignment?.find((a: any) => a.worker?.worker_id === workerId);
      if (assignment) setSelectedAssignment(assignment);
    }
    setView('replace_worker');
  };

  useEffect(() => {
    if (shiftId) {
      dispatch(fetchShiftDetails(shiftId));
      setView('details');
    }
  }, [shiftId, dispatch]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (!shift || !shiftId) return null;

  const isFull = shift.assigned_workers && shift.shift_assignee_count >= shift.workers_required;
  // const isCompleted = shift.status.toLowerCase() === 'completed'; // Replaced by complexStatuses

  if (view === 'manual_assign') {
    return (
      <ManualAssign
        shift={shift}
        onCancel={() => setView('details')}
        onAssign={(workerIds) => {
          console.log("Assigning workers:", workerIds);
          // Dispatch was handled in ManualAssign, just close
          setView('details');
          // Refresh details and grid
          if (shiftId) dispatch(fetchShiftDetails(shiftId));
          if (onRefresh) onRefresh();
        }}
      />
    );
  }

  if (view === 'replace_worker' && selectedAssignment) {
    return (
      <ReplaceWorker
        shift={shift}
        currentWorkerId={selectedAssignment.worker?.worker_id}
        onCancel={() => setView('worker_detail')}
        onSuccess={() => {
          if (shiftId) dispatch(fetchShiftDetails(shiftId));
          setView('details');
          if (onRefresh) onRefresh();
        }}
      />
    );
  }

  if (view === 'worker_detail' && selectedAssignment) {
    const user = selectedAssignment.worker.user || selectedAssignment.user || {};
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown Worker';

    return (
      <Box>
        <Box display="flex" alignItems="center" mb={1}>
          <IconButton onClick={() => setView('details')} size="small">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" fontWeight="bold" ml={1}>
            Shift Details
          </Typography>
        </Box>

        <WorkerAssigned
          assignedWorkerName={name}
          positionRole={shift.position_role}
          workerId={selectedAssignment.worker?.worker_id}
          avatarSrc={user.profile_picture}
          status={selectedAssignment.status === 'worker_cancelled' ? 'Worker Cancelled' : (selectedAssignment.status === 'facility_reject' ? 'Facility Reject' : (selectedAssignment.late_arrival ? 'Late' : 'On Time'))}
          isLate={selectedAssignment.status === 'facility_reject' || selectedAssignment.late_arrival || selectedAssignment.status === 'worker_cancelled'}
          onFlagWorker={
            (selectedAssignment.worker?.worker_id || selectedAssignment.worker_id)
              ? () => handleFlagWorkerClick(selectedAssignment.worker?.worker_id || selectedAssignment.worker_id)
              : undefined
          }
          onReplaceWorker={
            (selectedAssignment.worker?.worker_id || selectedAssignment.worker_id)
              ? () => handleReplaceWorkerStart(selectedAssignment.worker?.worker_id || selectedAssignment.worker_id)
              : undefined
          }
          onClockIn={() => handleClockIn(selectedAssignment.assignment_id || selectedAssignment.id)}
          onClockOut={() => handleClockOut(selectedAssignment.assignment_id || selectedAssignment.id)}
          isClockedIn={!!selectedAssignment.clock_in_time}
          isClockedOut={!!selectedAssignment.clock_out_time}
          workerRemarks={selectedAssignment.status === 'worker_cancelled' ? (selectedAssignment.worker_remarks || selectedAssignment.remarks) : undefined}
        />
        <Divider />
        <FacilityDetails
          facilityName={facilityName || String(shift.facility)}
          address={(shift.facility as any)?.address}
          latitude={(shift.facility as any)?.latitude}
          longitude={(shift.facility as any)?.longitude}
        />
        <Divider />
        <ShiftDetails
          date={dayjs(shift.start_date_time).format('DD/MM/YYYY')}
          createdDate={shift.created_on ? dayjs(shift.created_on).format('DD/MM/YYYY') : undefined}
          assignedDate={selectedAssignment.created_on ? dayjs(selectedAssignment.created_on).format('DD/MM/YYYY') : undefined} // Mock assignment date
          startTime={dayjs(shift.start_date_time).format('HH.mm')}
          endTime={dayjs(shift.end_date_time).format('HH.mm')}
          numberOfWorkers={`${shift.shift_assignment?.filter((a: any) => a.status !== 'worker_cancelled').length || 0}/${shift.workers_required}`}
          experience={0}
          appointedMember={"-"}
          position={shift.position_role || "-"}
          shift_id={shiftId}
          isSleepover={shift.is_sleepover}
          label={shift.label}
          // Detailed Props for specific worker
          assignmentStatus={(['partially_approval_pending', 'approval_pending'].includes(selectedAssignment.status) ? 'Facility Approval Pending' : (selectedAssignment.status === 'upcoming' ? 'Scheduled' : (selectedAssignment.status === 'unassigned' ? 'Unconfirmed' : selectedAssignment.status?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())))) || '-'}
          isFacilityReject={selectedAssignment.status === 'facility_approval_pending'}
          onApproveAttendance={selectedAssignment.status === 'facility_approval_pending' ? () => setIsVerifyModalOpen(true) : undefined}
          complianceStatus={(selectedAssignment.status === 'facility_reject' || selectedAssignment.late_arrival || selectedAssignment.left_early) ? 'Non-Compliant' : 'Compliant'}
          clockInTime={selectedAssignment.clock_in_time ? dayjs(selectedAssignment.clock_in_time).format('HH.mm') : '-'}
          clockOutTime={selectedAssignment.clock_out_time ? dayjs(selectedAssignment.clock_out_time).format('HH.mm') : '-'}
          totalHours={
            selectedAssignment.actual_hours_worked
              ? `${selectedAssignment.actual_hours_worked} hours`
              : `${dayjs(shift.end_date_time).diff(dayjs(shift.start_date_time), 'hour', true).toFixed(1)} hours`
          }
          checkInMethod={"Mobile App (Android)"}
          isLate={selectedAssignment.late_arrival}
          isEarly={selectedAssignment.left_early}
          workerRemarks={selectedAssignment.status === 'worker_cancelled' ? (selectedAssignment.worker_remarks || selectedAssignment.remarks) : undefined}
        // mapImageUrl={mapUrl} // Removed to avoid duplicate map (already in FacilityDetails)
        />
        <ShiftActions
          onAssign={() => setView('manual_assign')}
          onBroadcast={handleBroadcast}
          onEdit={() => onEdit && onEdit(shift)}
          onCancel={onCancelShiftClick}
          onExtend={() => setOpenExtendModal(true)}
          onApprove={handleApproveShift}
          onClockIn={() => handleClockIn(selectedAssignment.assignment_id || selectedAssignment.id)}
          onClockOut={() => handleClockOut(selectedAssignment.assignment_id || selectedAssignment.id)}
          isClockedIn={!!selectedAssignment.clock_in_time}
          isClockedOut={!!selectedAssignment.clock_out_time}
          hideExtend={true}
          hideEdit={true}
          shiftStatus={shift.status}
          isFull={!!isFull}
          endTime={shift.end_date_time}
        />
        <ActionModal
          open={isVerifyModalOpen}
          onClose={() => setIsVerifyModalOpen(false)}
          height="auto"
        >
          {selectedAssignment && shiftId && (
            <VerifyShift
              clockIn={selectedAssignment.clock_in_time}
              clockOut={selectedAssignment.clock_out_time}
              workerRemarks={selectedAssignment.worker_remarks}
              workerSuggestedTime={selectedAssignment.worker_suggested_time}
              assignmentId={selectedAssignment.assignment_id || selectedAssignment.id}
              shiftId={Number(shiftId)}
              shiftStart={shift.start_date_time}
              shiftEnd={shift.end_date_time}
              workerId={selectedAssignment.worker?.worker_id}
              isOrganizationView={true}
              onSuccess={(action, message) => {
                setIsVerifyModalOpen(false);
                if (shiftId) dispatch(fetchShiftDetails(shiftId));
                onRefresh && onRefresh();
                setAlertState({ open: true, severity: 'success', message: message || 'Attendance verified successfully' });
              }}
            />
          )}
        </ActionModal>
        <ConfirmationDialog
          open={flagConfirmationOpen}
          onClose={() => setFlagConfirmationOpen(false)}
          onConfirm={confirmFlagWorker}
          title="Flag Worker"
          message="Are you sure you want to flag this worker? This action will mark a violation against the worker."
          loading={isFlagging}
        />
        <AlertPopup
          open={alertState.open}
          severity={alertState.severity}
          message={alertState.message}
          onClose={() => setAlertState(prev => ({ ...prev, open: false }))}
        />
        <Dialog
          open={openApproveConfirmation}
          onClose={() => !loading && setOpenApproveConfirmation(false)}
          maxWidth="sm"
          fullWidth
          sx={{ zIndex: 1400 }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Approve Shift Attendance</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Are you sure you want to approve the attendance for this shift? You can optionally add remarks below.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Remarks (Optional)"
              type="text"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={approvalRemarks}
              onChange={(e) => setApprovalRemarks(e.target.value)}
              disabled={loading}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={() => setOpenApproveConfirmation(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={onConfirmApprove}
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ backgroundColor: '#52786F', '&:hover': { backgroundColor: '#3E5C54' } }}
            >
              {loading ? 'Approving...' : 'Confirm Approval'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  const complexStatuses = ['completed', 'partially_completed', 'partially_approval_pending', 'approval_pending', 'facility_approval_pending'];
  const showComplexView = complexStatuses.includes(shift.status.toLowerCase());

  // 1. Complex/Completed View OR Fully Staffed View -> Show List
  if (showComplexView || isFull) {
    return (
      <Box>
        <ShiftDetails
          date={dayjs(shift.start_date_time).format('DD/MM/YYYY')}
          createdDate={shift.created_on ? dayjs(shift.created_on).format('DD/MM/YYYY') : undefined}
          assignedDate={shift.shift_assignment?.length > 0 && shift.shift_assignment[0].created_on ? dayjs(shift.shift_assignment[0].created_on).format('DD/MM/YYYY') : undefined}
          startTime={dayjs(shift.start_date_time).format('HH.mm')}
          endTime={dayjs(shift.end_date_time).format('HH.mm')}
          numberOfWorkers={`${shift.shift_assignment?.filter((a: any) => a.status !== 'worker_cancelled').length || 0}/${shift.workers_required}`}
          experience={0}
          appointedMember={"-"}
          position={shift.position_role || "-"}
          shift_id={shiftId}
          isSleepover={shift.is_sleepover}
          label={shift.label}
        />
        <Divider />
        <FacilityDetails
          facilityName={facilityName || String(shift.facility)}
          address={(shift.facility as any)?.address}
          latitude={(shift.facility as any)?.latitude}
          longitude={(shift.facility as any)?.longitude}
        />
        <Divider />
        {/* Pass onWorkerClick to allow drill-down */}
        <AssignedWorkerList
          shift={shift}
          onWorkerClick={handleWorkerClick}
          variant={showComplexView ? 'simple' : 'detailed'}
        />
        {shift.status?.toLowerCase() !== 'cancelled' && (
          <ShiftActions
            onAssign={() => setView('manual_assign')}
            onBroadcast={handleBroadcast}
            onEdit={() => onEdit && onEdit(shift)}
            onCancel={onCancelShiftClick}
            onExtend={() => setOpenExtendModal(true)}
            onApprove={handleApproveShift}
            isClockedIn={shift.shift_assignment?.length === 1 && !!shift.shift_assignment[0].clock_in_time}
            isClockedOut={shift.shift_assignment?.length === 1 && !!shift.shift_assignment[0].clock_out_time}
            shiftStatus={shift.status}
            isFull={!!isFull}
            endTime={shift.end_date_time}
          />
        )}
        
        {shift.status?.toLowerCase() === 'cancelled' && (
          <ShiftActions
            onAssign={() => setView('manual_assign')}
            onBroadcast={handleBroadcast}
            onEdit={() => onEdit && onEdit(shift)}
            onCancel={onCancelShiftClick}
            onDelete={() => setOpenDeleteConfirmation(true)}
            onExtend={() => setOpenExtendModal(true)}
            shiftStatus={shift.status}
            isFull={!!isFull}
            endTime={shift.end_date_time}
          />
        )}
        <ActionModal
          open={openActionModal}
          onClose={() => setOpenActionModal(false)}
          height="70vh"
        >
          <CancelShift
            onCancelShift={handleCancelSubmit}
            onKeepShift={() => setOpenActionModal(false)}
            shiftId={shiftId ? Number(shiftId) : undefined}
          />
        </ActionModal>
        <ActionModal
          open={openExtendModal}
          onClose={() => setOpenExtendModal(false)}
          height="auto"
        >
          <ExtendShift
            onCancel={() => setOpenExtendModal(false)}
            onExtend={handleExtendSubmit}
            loading={extending}
            shiftEndTime={shift.end_date_time}
          />
        </ActionModal>
        <ActionModal
          open={openValidationModal}
          onClose={() => setOpenValidationModal(false)}
          height="auto"
        >
          <ValidationConflicts
            conflicts={validationConflicts}
            onCancel={() => setOpenValidationModal(false)}
            onProceed={handleValidationProceed}
          />
        </ActionModal>
        <ConfirmationDialog
          open={openConfirmation}
          onClose={() => setOpenConfirmation(false)}
          onConfirm={handleConfirmCancel}
          title="Confirm Cancellation"
          message="Are you sure you want to cancel this shift?"
        />
        <ConfirmationDialog
          open={openForceConfirmation}
          onClose={() => setOpenForceConfirmation(false)}
          onConfirm={handleForceCancel}
          title="Force Cancellation Required"
          message="Shift cancellation time has passed. Charges may apply. Do you want to proceed with force cancellation?"
        />
        <ConfirmationDialog
          open={flagConfirmationOpen}
          onClose={() => setFlagConfirmationOpen(false)}
          onConfirm={confirmFlagWorker}
          title="Flag Worker"
          message="Are you sure you want to flag this worker? This action will mark a violation against the worker.23e2"
          loading={isFlagging}
        />
        <AlertPopup
          open={alertState.open}
          severity={alertState.severity}
          message={alertState.message}
          onClose={() => setAlertState(prev => ({ ...prev, open: false }))}
        />
        <Dialog
          open={openApproveConfirmation}
          onClose={() => !loading && setOpenApproveConfirmation(false)}
          maxWidth="sm"
          fullWidth
          sx={{ zIndex: 1400 }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Approve Shift Attendance</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Are you sure you want to approve the attendance for this shift? You can optionally add remarks below.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Remarks (Optional)"
              type="text"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={approvalRemarks}
              onChange={(e) => setApprovalRemarks(e.target.value)}
              disabled={loading}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={() => setOpenApproveConfirmation(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={onConfirmApprove}
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ backgroundColor: '#52786F', '&:hover': { backgroundColor: '#3E5C54' } }}
            >
              {loading ? 'Approving...' : 'Confirm Approval'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    )
  }

  // 3. Default/Pending View (Actionable)
  return (
    <Box>
      <WorkerAssigned
        assignedWorkerName={shift.assigned_workers}
        positionRole={shift.position_role}
        isClockedIn={shift.shift_assignment?.length === 1 && !!shift.shift_assignment[0].clock_in_time}
        isClockedOut={shift.shift_assignment?.length === 1 && !!shift.shift_assignment[0].clock_out_time}
      />

      <Divider />

      <FacilityDetails
        facilityName={facilityName || String(shift.facility)}
        address={(shift.facility as any)?.address}
        latitude={(shift.facility as any)?.latitude}
        longitude={(shift.facility as any)?.longitude}
      />

      <Divider />

      <ShiftDetails
        date={dayjs(shift.start_date_time).format('DD/MM/YYYY')}
        createdDate={shift.created_on ? dayjs(shift.created_on).format('DD/MM/YYYY') : undefined}
        startTime={dayjs(shift.start_date_time).format('HH.mm')}
        endTime={dayjs(shift.end_date_time).format('HH.mm')}
        numberOfWorkers={`${shift.shift_assignment?.filter((a: any) => a.status !== 'worker_cancelled').length || 0}/${shift.workers_required}`}
        experience={`${shift.experience_required || 0}+ years`}
        appointedMember={"-"}
        position={shift.position_role || "-"}
        shift_id={shiftId}
        isSleepover={shift.is_sleepover}
        label={shift.label}
      />

      {shift.status?.toLowerCase() !== 'cancelled' && (
        <ShiftActions
          onAssign={() => setView('manual_assign')}
          onBroadcast={handleBroadcast}
          onEdit={() => onEdit && onEdit(shift)}
          onCancel={onCancelShiftClick}
          onExtend={() => setOpenExtendModal(true)}
          onApprove={handleApproveShift}
          isClockedIn={shift.shift_assignment?.length === 1 && !!shift.shift_assignment[0].clock_in_time}
          isClockedOut={shift.shift_assignment?.length === 1 && !!shift.shift_assignment[0].clock_out_time}
          shiftStatus={shift.status}
          isFull={!!isFull}
          endTime={shift.end_date_time}
        />
      )}
      
      {shift.status?.toLowerCase() === 'cancelled' && (
        <ShiftActions
          onAssign={() => setView('manual_assign')}
          onBroadcast={handleBroadcast}
          onEdit={() => onEdit && onEdit(shift)}
          onCancel={onCancelShiftClick}
          onDelete={() => setOpenDeleteConfirmation(true)}
          onExtend={() => setOpenExtendModal(true)}
          shiftStatus={shift.status}
          isFull={!!isFull}
          endTime={shift.end_date_time}
        />
      )}
      <ActionModal
        open={openActionModal}
        onClose={() => setOpenActionModal(false)}
        height="70vh"
      >
        <CancelShift
          onCancelShift={handleCancelSubmit}
          onKeepShift={() => setOpenActionModal(false)}
          shiftId={shiftId ? Number(shiftId) : undefined}
        />
      </ActionModal>
      <ActionModal
        open={openExtendModal}
        onClose={() => setOpenExtendModal(false)}
        height="auto"
      >
        <ExtendShift
          onCancel={() => setOpenExtendModal(false)}
          onExtend={handleExtendSubmit}
          loading={extending}
          shiftEndTime={shift.end_date_time}
        />
      </ActionModal>
      <ActionModal
        open={openValidationModal}
        onClose={() => setOpenValidationModal(false)}
        height="auto"
      >
        <ValidationConflicts
          conflicts={validationConflicts}
          onCancel={() => setOpenValidationModal(false)}
          onProceed={handleValidationProceed}
        />
      </ActionModal>
      <ConfirmationDialog
        open={openConfirmation}
        onClose={() => setOpenConfirmation(false)}
        onConfirm={handleConfirmCancel}
        title="Confirm Cancellation"
        message="Are you sure you want to cancel this shift?"
      />
      <ConfirmationDialog
        open={openForceConfirmation}
        onClose={() => setOpenForceConfirmation(false)}
        onConfirm={handleForceCancel}
        title="Force Cancellation Required"
        message="Shift cancellation time has passed. Charges may apply. Do you want to proceed with force cancellation?"
      />
      <ConfirmationDialog
        open={flagConfirmationOpen}
        onClose={() => setFlagConfirmationOpen(false)}
        onConfirm={confirmFlagWorker}
        title="Flag Worker"
        message="Are you sure you want to flag this worker? This action will mark a violation against the worker.23e2"
        loading={isFlagging}
      />
      <ConfirmationDialog
        open={openDeleteConfirmation}
        onClose={() => setOpenDeleteConfirmation(false)}
        onConfirm={handleDeleteShift}
        title="Confirm Delete"
        message="Are you sure you want to delete this cancelled shift?"
        loading={isDeleting}
      />

      <Dialog
        open={openApproveConfirmation}
        onClose={() => !loading && setOpenApproveConfirmation(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1400 }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Approve Shift Attendance</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to approve the attendance for this shift? You can optionally add remarks below.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Remarks (Optional)"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={approvalRemarks}
            onChange={(e) => setApprovalRemarks(e.target.value)}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setOpenApproveConfirmation(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={onConfirmApprove}
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ backgroundColor: '#52786F', '&:hover': { backgroundColor: '#3E5C54' } }}
          >
            {loading ? 'Approving...' : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertPopup
        open={alertState.open}
        severity={alertState.severity}
        message={alertState.message}
        onClose={() => setAlertState(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default ShiftDetailContent;
